import { Fetcher } from '@this/src/util';
import React, { useEffect, useMemo, useState } from 'react';
import { Redirect } from 'react-router-dom';
import { reportError } from '@this/lib/bugsnag';
import { Link } from '@this/shared/ui/navigations/link';
import { createColors } from '../../shared/ui/theme/tokens/primitive-tokens/colors';
import { TextField } from '../../shared/form_elements/form_elements';
import { Loading } from '../../shared/ui/feedbacks/loading/loading';
import { isIncludeHyphen } from '../../shared/checker/hyphen_checker';

import type User from '../../../domain/user/user';

const brandColors = createColors(1).brand;

const EMAIL_REGEXP = /^[^\s@]+@([^\s@]+\.)+[^\s@]+$/;
const PASSWORD_MIN_LENGTH = 10;

interface Props {
  user: User | null | undefined;
}

interface FormErrors {
  company?: string;
  email?: string;
  password?: string;
  last_name?: string;
  first_name?: string;
  tel?: string;
  base?: string;
}

const ExpensesUsersSignup = ({ user }: Props) => {
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [lastName, setLastName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [tel, setTel] = useState('');
  const [isPrivacyChecked, setIsPrivacyChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [succeed, setSucceed] = useState(false);
  const styles = useMemo(() => getStyles(), []);

  useEffect(() => {
    mixpanel.track('[Biztra][view] /users/sign_up');
  }, []);

  const handleTelChange = (value: string) => {
    setErrors(prev => ({
      ...prev,
      tel: isIncludeHyphen(value) ? 'ハイフンを含めず入力してください' : undefined
    }));
    setTel(value);
  };

  const isValidForm = (): FormErrors => {
    const errs: FormErrors = {};
    if (!company) errs.company = '会社名を入力してください';
    if (!lastName) errs.last_name = '姓を入力してください';
    if (!firstName) errs.first_name = '名を入力してください';
    if (!email) {
      errs.email = 'メールアドレスを入力してください';
    } else if (!EMAIL_REGEXP.test(email)) {
      errs.email = '正しいメールアドレスを入力してください';
    }
    if (!password) {
      errs.password = 'パスワードを入力してください';
    } else if (password.length < PASSWORD_MIN_LENGTH) {
      errs.password = `パスワードは${PASSWORD_MIN_LENGTH}文字以上で入力してください`;
    }
    if (!tel) {
      errs.tel = '電話番号を入力してください';
    } else if (isIncludeHyphen(tel)) {
      errs.tel = 'ハイフンを含めず入力してください';
    }
    return errs;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const validationErrors = isValidForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setSubmitting(true);
    setErrors({});
    setSubmitError(null);

    try {
      await Fetcher.post('/aiexpense/users.json', {
        company,
        email,
        password,
        last_name: lastName,
        first_name: firstName,
        tel
      });

      setSubmitting(false);
      setSucceed(true);
      mixpanel.track('[Biztra] sign up mail sent');
    } catch (e) {
      if (e.response?.status === 400) {
        setSubmitting(false);
        setErrors(e.response.data.errors);
        mixpanel.track('[Biztra] sign up error', { errors: e.response.data.errors });
      } else {
        setSubmitting(false);
        setSubmitError('通信エラーが発生しました。時間をおいて再度お試しください。');
      }
    }
  };

  try {
    return (
      // NOTE: TextField の色・submit ボタン色は SCSS カスケード（.expenses-users-signup スコープ）で制御するためクラスを保持
      <div className="expenses-users-signup" style={styles.container}>
        <div style={styles.content}>
          {user ? (
            <Redirect to="/aiexpense/report_items" />
          ) : (
            <>
              <div style={styles.tagline}>今すぐ使ってみる</div>
              {succeed ? (
                <div className="gray">
                  仮登録完了のメールを送信しました。
                  <br />
                  メールの内容に従って本登録を行ってください。
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <TextField
                    label="会社名（必須）"
                    value={company}
                    example="例：株式会社トランスファーデータ"
                    error={errors.company}
                    onChange={setCompany}
                  />
                  <div style={styles.nameFieldRow}>
                    <div style={styles.nameFieldFirst}>
                      <TextField
                        label="姓（必須）"
                        value={lastName}
                        example="例：山田"
                        error={errors.last_name}
                        onChange={setLastName}
                      />
                    </div>
                    <div style={styles.nameField}>
                      <TextField
                        label="名（必須）"
                        value={firstName}
                        example="例：一郎"
                        error={errors.first_name}
                        onChange={setFirstName}
                      />
                    </div>
                  </div>
                  <TextField
                    label="メールアドレス（必須）"
                    value={email}
                    example="例：biztra@aitravel.jp"
                    error={errors.email}
                    onChange={setEmail}
                  />
                  <TextField
                    label="パスワード（必須）"
                    value={password}
                    example=""
                    type="password"
                    error={errors.password}
                    onChange={setPassword}
                  />
                  <TextField
                    label="電話番号（必須）"
                    value={tel}
                    example="例：0312345678"
                    error={errors.tel}
                    onChange={handleTelChange}
                  />
                  {errors.base && <div className="error">{errors.base}</div>}
                  <p style={styles.termsLink}>
                    <input type="checkbox" onClick={() => setIsPrivacyChecked(!isPrivacyChecked)} />
                    <a
                      href="https://transferdata.co.jp/privacy-policy"
                      target="_blank"
                      rel="noreferrer noopener"
                      style={styles.termsLinkAnchor}
                    >
                      利用規約・個人情報の取扱いについて
                    </a>
                    に同意の上、ご登録ください
                  </p>
                  {submitting ? <Loading /> : <input type="submit" value="登録" disabled={!isPrivacyChecked} />}
                  {submitError && <div className="error">{submitError}</div>}
                </form>
              )}
            </>
          )}
        </div>
        <div style={styles.footer}>
          <Link href="/aiexpense/users/sign_in">すでにアカウントをお持ちの方はこちら</Link>
        </div>
      </div>
    );
  } catch (e) {
    reportError(e);
    return null;
  }
};

const getStyles = () => ({
  container: {
    maxWidth: '600px',
    width: '100%',
    margin: '50px auto'
  },
  content: {
    height: 'fit-content' as const,
    padding: '20px',
    background: 'white',
    fontSize: '16px',
    fontWeight: 'bold' as const
  },
  tagline: {
    fontSize: '24px',
    fontWeight: 'bold' as const,
    marginBottom: '40px',
    textAlign: 'center' as const
  },
  nameFieldRow: {
    display: 'flex',
    alignItems: 'center' as const,
    marginBottom: '40px'
  },
  nameFieldFirst: {
    flexGrow: 999,
    marginRight: '20px'
  },
  nameField: {
    flexGrow: 999
  },
  termsLink: {
    color: '#7d7d7d',
    fontWeight: 'normal' as const,
    marginBottom: '30px'
  },
  termsLinkAnchor: {
    color: brandColors[500]
  },
  footer: {
    marginTop: '10px',
    textAlign: 'center' as const
  }
});

export default ExpensesUsersSignup;
